/**
 * Estado en vivo de la plantilla de una campaña en borrador (SPEC-BD C2): el operador sigue con la
 * audiencia y las variables mientras Meta la revisa, y esta tarjeta le dice cuándo cambió.
 *
 * - Mientras está en revisión (`received`/`pending`) consulta sola: en simulado con `POST sincronizar`
 *   cada 5 s (es lo que resuelve la aprobación simulada al vencer su plazo); con Twilio, `GET` cada
 *   30 s, que lee lo que ya actualizan el evento de Twilio y el scheduler sin pegarle a Twilio.
 * - Los errores NO cortan la consulta (a diferencia del resto de las pantallas): una aprobación puede
 *   tardar 48 h y un corte de red de un minuto no puede dejar la tarjeta congelada en «En revisión».
 *   Con 3 fallas seguidas se avisa, y se sigue intentando.
 * - No confirma ni manda nada: sólo avisa con `onCambio` (C2). La prueba y el envío los habilita el
 *   asistente cuando la plantilla ya está aprobada.
 */

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Copy, Loader2, OctagonAlert, Pencil, RefreshCw, Send } from "lucide-react";
import type { EstadoPlantilla, ModoTransporte, PlantillaDetalle } from "../types";
import { oficialApi } from "../api";
import { usePolling } from "../hooks/usePolling";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { ESTADO_PLANTILLA } from "../logic/textos";

export const INTERVALO_SIMULADO_MS = 5_000;
export const INTERVALO_TWILIO_MS = 30_000;
/** Después de «Consultar a Meta», el botón descansa: Twilio no tiene nada nuevo cada segundo. */
export const ESPERA_CONSULTA_MANUAL_MS = 15_000;
const FALLAS_PARA_AVISAR = 3;

export interface PlantillaEnVivo { id: string; estado: EstadoPlantilla; rejectionReason?: string; nombreVisible: string }

interface Props {
  plantilla: PlantillaEnVivo;
  modo: ModoTransporte;
  onCambio: (p: PlantillaDetalle) => void;
  /** Rechazada, o en revisión con un error: duplicarla y abrir la copia en el editor (la campaña pasa a usar la copia). */
  onCorregir: () => void;
  onSeguirEditando: () => void;
  /** «Duplicar y editar» en curso (duplicando): el botón muestra el spinner. */
  corrigiendo?: boolean;
}

const enRevision = (e: EstadoPlantilla) => e === "received" || e === "pending";

export function EstadoPlantillaEnVivo({ plantilla, modo, onCambio, onCorregir, onSeguirEditando, corrigiendo = false }: Props) {
  // Lo último que se sabe: arranca con lo que trae quien lo muestra y se actualiza con cada consulta,
  // así la consulta frena sola al aprobarse aunque el padre tarde en re-renderizar.
  const [actual, setActual] = useState<PlantillaEnVivo>(plantilla);
  const [fallas, setFallas] = useState(0);
  const [consultando, setConsultando] = useState(false);
  const [descansoHasta, setDescansoHasta] = useState(0);
  const [reintentando, setReintentando] = useState(false);
  const [errorReintento, setErrorReintento] = useState("");
  const onCambioRef = useRef(onCambio);
  onCambioRef.current = onCambio;
  // Una prop con OTROS datos reemplaza lo que se sabía. Se compara por valor y no por referencia: el
  // asistente arma `plantilla` inline en cada render, y reiniciar con cada render del padre borraría
  // las fallas y volvería al estado viejo del padre. Se ajusta en el render (no en un efecto) para
  // que no haya un render con la plantilla nueva en la prop y la anterior en `actual`.
  const firma = [plantilla.id, plantilla.estado, plantilla.rejectionReason ?? "", plantilla.nombreVisible].join("\u0000");
  const [firmaVista, setFirmaVista] = useState(firma);
  if (firma !== firmaVista) {
    setFirmaVista(firma);
    setActual(plantilla);
    setFallas(0);
    setErrorReintento("");
  }
  const actualRef = useRef(actual);
  actualRef.current = actual;

  /** Aplica una respuesta: avisa sólo si cambió algo que el asistente mira (estado o motivo). */
  const aplicar = (p: PlantillaDetalle) => {
    const antes = actualRef.current;
    // La respuesta de una consulta que salió para la plantilla ANTERIOR (el operador eligió otra con
    // el pedido en vuelo) no es de ésta: aplicarla mostraba «Aprobada» sobre la nueva y dejaba la
    // consulta siguiendo a la vieja.
    if (p.id && p.id !== antes.id) return;
    const nuevo: PlantillaEnVivo = { id: p.id || antes.id, estado: p.estado, rejectionReason: p.rejectionReason, nombreVisible: p.nombreVisible || antes.nombreVisible };
    setActual(nuevo);
    if (nuevo.estado !== antes.estado || (nuevo.rejectionReason || "") !== (antes.rejectionReason || "")) onCambioRef.current(p);
  };

  /** Una consulta. Nunca lanza: una falla se cuenta y la cadena sigue. */
  const consultar = async (forzarSincronizar = false) => {
    const id = actualRef.current.id;
    try {
      const r = modo === "simulated" || forzarSincronizar ? await oficialApi.sincronizarPlantilla(id) : await oficialApi.plantilla(id);
      const l = leerRespuesta<PlantillaDetalle>(r);
      if (!l.ok || !l.data || typeof l.data.estado !== "string") { setFallas((n) => n + 1); return; }
      setFallas(0);
      aplicar(l.data);
    } catch {
      setFallas((n) => n + 1);
    }
  };

  const intervalo = enRevision(actual.estado) ? (modo === "simulated" ? INTERVALO_SIMULADO_MS : INTERVALO_TWILIO_MS) : null;
  usePolling(() => consultar(), intervalo, [actual.id, modo]);

  // El descanso del botón manual se apaga solo.
  useEffect(() => {
    if (!descansoHasta) return;
    const t = setTimeout(() => setDescansoHasta(0), Math.max(0, descansoHasta - Date.now()));
    return () => clearTimeout(t);
  }, [descansoHasta]);

  const consultarAhora = async () => {
    if (consultando || descansoHasta) return;
    setConsultando(true);
    setDescansoHasta(Date.now() + ESPERA_CONSULTA_MANUAL_MS);
    try { await consultar(true); } finally { setConsultando(false); }
  };

  const reintentarEnvio = async () => {
    if (reintentando) return;
    setReintentando(true);
    setErrorReintento("");
    try {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.enviarAAprobacion(actual.id));
      if (!l.ok) { setErrorReintento(l.error || textoError(l)); return; }
      if (l.data) aplicar(l.data);
    } catch (e) {
      setErrorReintento(textoError(leerError(e)));
    } finally {
      setReintentando(false);
    }
  };

  const avisoFallas = fallas >= FALLAS_PARA_AVISAR && (
    <p className="mt-2 flex items-center justify-between gap-3 text-xs text-red-700" role="alert">
      <span>No pudimos consultar el estado.</span>
      <button type="button" onClick={() => void consultar()} className="font-semibold underline">Reintentar</button>
    </p>
  );

  const e = actual.estado;
  if (enRevision(e)) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status" data-testid="estado-plantilla">
        <p className="flex items-start gap-2">
          <Clock size={15} className="mt-0.5 flex-shrink-0" />
          <span>
            En revisión por Meta. Suele tardar de minutos a 48 h{modo === "simulated" ? " (en modo simulado, unos segundos)" : ""}. Mientras tanto podés completar las variables y elegir cuándo sale; la prueba y el envío se habilitan cuando la aprueben.
          </span>
        </p>
        {modo === "twilio" && (
          <button type="button" onClick={() => void consultarAhora()} disabled={consultando || !!descansoHasta}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50">
            {consultando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Consultar a Meta
          </button>
        )}
        {/* Ya mandada no se edita (Meta revisa ESE texto): un error se arregla en una copia (Llama Leads, 26/10/2026). */}
        <p className="mt-2 text-xs" data-testid="corregir-en-revision">
          ¿Te equivocaste en algo? Mientras Meta la revisa no se puede editar: duplicala, corregí la copia y mandala de nuevo. La campaña pasa a usar la copia.
        </p>
        <button type="button" onClick={onCorregir} disabled={corrigiendo}
          className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50">
          {corrigiendo ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} Duplicar y editar
        </button>
        {avisoFallas}
      </div>
    );
  }
  if (e === "approved") {
    return (
      <p className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status" data-testid="estado-plantilla">
        <CheckCircle2 size={15} /> Aprobada por Meta.
      </p>
    );
  }
  if (e === "rejected") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status" data-testid="estado-plantilla">
        <p className="flex items-start gap-2"><OctagonAlert size={15} className="mt-0.5 flex-shrink-0" /> Meta la rechazó: {actual.rejectionReason?.trim() || "no dio el motivo"}.</p>
        <button type="button" onClick={onCorregir} disabled={corrigiendo}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-white border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-100 disabled:opacity-50">
          {corrigiendo ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />} Duplicar y editar
        </button>
      </div>
    );
  }
  if (e === "unsubmitted") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status" data-testid="estado-plantilla">
        <p className="flex items-start gap-2"><AlertTriangle size={15} className="mt-0.5 flex-shrink-0" /> No se pudo mandar a Meta.</p>
        <button type="button" onClick={() => void reintentarEnvio()} disabled={reintentando}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-white border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-100 disabled:opacity-50">
          {reintentando ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Reintentar el envío
        </button>
        {errorReintento && <p className="mt-2 text-xs" role="alert">{errorReintento}</p>}
      </div>
    );
  }
  if (e === "borrador") {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" role="status" data-testid="estado-plantilla">
        <p>Todavía no se mandó a Meta.</p>
        <button type="button" onClick={onSeguirEditando}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-white border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100">
          <Pencil size={13} /> Seguir editando
        </button>
      </div>
    );
  }
  // paused, disabled, eliminada (o un estado nuevo que el front no conoce): no se puede usar.
  const info = ESTADO_PLANTILLA[e];
  return (
    <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="status" data-testid="estado-plantilla">
      <strong>{info?.label || String(e || "Sin estado")}.</strong> {info?.ayuda || "Esta plantilla no se puede usar."}
    </p>
  );
}

export default EstadoPlantillaEnVivo;
